import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";


import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_SUPPLIERORDER_STATUSES } from "../../graphql/queries/IncomingOrdersContext";
import {
  CREATE_UPDATE_SUPPLIERORDER_STATUS,
  UPDATE_SUPPLIER_STATUS_SORTORDER,
  DELETE_SUPPLIER_ORDER_STATUS 
} from "../../graphql/mutations";

import { getCurrentUser } from "../../helpers/selectors";
import { PROCESS_CATEGORIES } from "./helpers/processCategories";
import { useSelector } from "react-redux";

import OrderStatusPanel from "./OrderStatusPanel";
import StatusRow from "./StatusRow";

function moveItemInList(list, fromIndex, toIndex) {
  const copy = [...list];
  const [moved] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, moved);
  return copy;
}

const StatusManagerDialog = ({statuses, open, onChange, onClose }) => {
  const { companyId } = useSelector(getCurrentUser);

  const [items, setItems] = useState(statuses || []);

  useEffect(() => {
    if (open) {
      setItems(statuses || []);
    }
  }, [statuses, open]);


  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [createCategory, setCreateCategory] = useState(PROCESS_CATEGORIES[0].key);



  /** CREATE / UPDATE */
  const [updateSupplierOrderStatus] = useMutation(CREATE_UPDATE_SUPPLIERORDER_STATUS);  
  const [updateStructure] = useMutation(UPDATE_SUPPLIER_STATUS_SORTORDER);
  const [deleteSupplierOrderStatus] = useMutation(DELETE_SUPPLIER_ORDER_STATUS);

  const saveStructure = async (list) => {
    await updateStructure({
      variables: {
        input: list.map((item, index) => ({
          id: item.id,
          productionStatus: item.productionStatus,          
        })),
      },
    });
  };

  const supplierStatuses = useMemo(() => PROCESS_CATEGORIES.map((c) => c.key), []);

  const existingNames = useMemo(() => items.map((s) => s.name), [items]);

  /** group by category */
  const byCategory = useMemo(() => {
    const map = new Map(PROCESS_CATEGORIES.map((c) => [c.key, []]));
    items.forEach((s) => {
      const key = s.productionStatus || "NEW";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    });
    return map;
  }, [items]);

  const visibleCategories = useMemo(
    () => PROCESS_CATEGORIES.filter((c) => (byCategory.get(c.key) || []).length > 0),
    [byCategory]
  );

  const openCreateFor = (categoryKey) => {
    setEditingItem({
      name: "",
      color: PROCESS_CATEGORIES.find((c) => c.key === categoryKey)?.color || "#B0BEC5",
      productionStatus: categoryKey,
      action: "Ingen åtgärd",
      orderStatus: "",
    });
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setEditorOpen(true);
  };

  const handleDelete = async (id) => {
    const target = items.find((x) => x.id === id);
    if (!target) return; 
    
    const newCategoryItems = byCategory.get("NEW") || [];
    if (target.productionStatus === "NEW" && newCategoryItems.length <= 1) {
      // cannot delete last NEW status
      alert("Det måste finnas minst en status kvar i kategorin 'Ny'.");
      return;
    }

    try {
      await deleteSupplierOrderStatus({ variables: { supplierStatusId: id } });
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (err) {
      console.error("Error deleting status:", err);
      alert("Kunde inte ta bort statusen. Försök igen.");
    }
  };

  const moveUpDown = (categoryKey, index, dir) => {
    const list = byCategory.get(categoryKey) || [];
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= list.length) return;
    moveWithinCategory(categoryKey, index, newIndex);
  };

  /** flatten tillbaka */
  const rebuildItemsFromCategoryMap = useCallback((map) => {
    const flattened = [];
    PROCESS_CATEGORIES.forEach((c) => {
      (map.get(c.key) || []).forEach((x) => flattened.push(x));
    });
    return flattened;
  }, []);

  /** DnD */
  const moveWithinCategory = useCallback(
    (categoryKey, fromIndex, toIndex) => {
      setItems((prev) => {
        const map = new Map(PROCESS_CATEGORIES.map((c) => [c.key, []]));
        prev.forEach((s) => {
          const k = s.productionStatus || "NEW";
          map.get(k).push(s);
        });

        const moved = moveItemInList(map.get(categoryKey), fromIndex, toIndex);
        map.set(categoryKey, moved);

        return rebuildItemsFromCategoryMap(map);
      });
    },
    [rebuildItemsFromCategoryMap]
  );

  const moveAcrossCategories = useCallback(
    (fromCategory, fromIndex, toCategory, toIndex) => {
      setItems((prev) => {
        const map = new Map(PROCESS_CATEGORIES.map((c) => [c.key, []]));
        prev.forEach((s) => {
          const k = s.productionStatus || "NEW";
          map.get(k).push(s);
        });

        const fromList = [...map.get(fromCategory)];
        const toList = [...map.get(toCategory)];

        const [moved] = fromList.splice(fromIndex, 1);
        const patched = { ...moved, productionStatus: toCategory };

        toList.splice(toIndex, 0, patched);

        map.set(fromCategory, fromList);
        map.set(toCategory, toList);

        return rebuildItemsFromCategoryMap(map);
      });
    },
    [rebuildItemsFromCategoryMap]
  );

  const handleSaveStatus = async (status) => {
    const { data } = await updateSupplierOrderStatus({
      variables: {
        input: {
          id: status.id || 0,
          companyId,
          name: status.name,
          color: status.color,
          action: status.action,
          actionTrigger: status.actionTrigger,
          productionStatus: status.productionStatus,
          userMustConfirmAction : status.userMustConfirmAction
        },
      },
    });

    const updated = data?.incomingOrders?.createUpdateSupplierOrderStatus?.result;

    setItems((prev) =>
      status.id ? prev.map((x) => (x.id === status.id ? updated : x)) : [...prev, updated]
    );

    setEditorOpen(false);
  };

  const [saving, setSaving] = useState(false);

  const handleCancel = () => {
    if (saving) return;
    onClose();
  };

  const handleDone = async () => {
    if (saving) return;

    try {
      setSaving(true);
      await saveStructure(items);
      onChange?.(items);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Hantera statusar
          <IconButton onClick={handleCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Skapa, redigera och sortera egna statusar. Statusar måste ligga i en processkategori.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={2}>
            {visibleCategories.map((cat) => {
              const list = byCategory.get(cat.key) || [];

              return (
                <Box key={cat.key}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                      px: 0.5,
                    }}
                  >
                    {/* <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: cat.color,
                        border: "1px solid rgba(0,0,0,0.18)",
                        flexShrink: 0,
                      }}
                    /> */}
                    <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: 0.2 }}>
                      {cat.label}
                    </Typography>
                  </Box>

                  <Stack spacing={0.25}>
                    {list.map((s, idx) => (
                      <StatusRow
                        key={s.id}
                        item={s}
                        indexWithinCategory={idx}
                        categoryKey={cat.key}
                        moveWithinCategory={(from, to) => moveWithinCategory(cat.key, from, to)}
                        moveAcrossCategories={moveAcrossCategories}
                        onEdit={() => openEdit(s)}
                        onDelete={() => handleDelete(s.id)}
                        onMoveUp={() => moveUpDown(cat.key, idx, -1)}
                        onMoveDown={() => moveUpDown(cat.key, idx, +1)}
                        isFirst={idx === 0}
                        isLast={idx === list.length - 1}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            sx={{ fontWeight: 700, letterSpacing: 0.2 }}
          >
            Ny status
          </Button>

          <Button variant="contained" onClick={handleDone} disabled={saving}>
            {saving ? "Sparar..." : "Klar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Välj kategori för ny status */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 260 } } }}
      >
        <Typography variant="caption" sx={{ px: 2, pt: 1.25, pb: 0.75, color: "text.secondary" }}>
          Välj processkategori
        </Typography>

        {PROCESS_CATEGORIES.map((c) => (
          <MenuItem
            key={c.key}
            onClick={() => {
              setCreateCategory(c.key);
              setAddMenuAnchor(null);
              openCreateFor(c.key);
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: c.color,
                  border: "1px solid rgba(0,0,0,0.18)",
                }}
              />
              <Typography sx={{ fontSize: 13 }}>{c.label}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <OrderStatusPanel
        open={editorOpen}
        mode={editingItem?.id ? "edit" : "create"}
        initialValue={editingItem}
        existingNames={existingNames}
        supplierStatuses={supplierStatuses}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveStatus}
      />
    </DndProvider>
  );
};

StatusManagerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

export default StatusManagerDialog;
